import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { LANDING_I18N } from '../i18n/landing';
import { TRUST_I18N } from '../i18n/trust';

// Tiny language context dedicated to the public landing page.
// We deliberately *do not* wrap the authenticated app — translating the
// Lean/CI platform itself is a much larger scope and out of bounds here.
// Default: English. Preference persists in localStorage under
// `kk.landing.lang`. Supported codes: 'nl' | 'en'.

const STORAGE_KEY = 'kk.landing.lang';
const DEFAULT_LANG = 'en';
const SUPPORTED = ['nl', 'en'];

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: { ...LANDING_I18N[DEFAULT_LANG], ...TRUST_I18N[DEFAULT_LANG] },
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED.includes(saved)) return saved;
    } catch (_) { /* localStorage may be blocked */ }
    return DEFAULT_LANG;
  });

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    // Update <html lang="..."> so screen readers + browser translate prompts
    // pick up the active language.
    try { document.documentElement.setAttribute('lang', lang); } catch (_) { /* noop */ }
  }, [lang]);

  const value = {
    lang,
    setLang,
    t: {
      ...(LANDING_I18N[lang] || LANDING_I18N[DEFAULT_LANG]),
      ...(TRUST_I18N[lang] || TRUST_I18N[DEFAULT_LANG]),
    },
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook accessor. Pass an optional section key (`hero`, `products`, etc.)
// to get a scoped sub-dictionary for a single component.
export const useLang = (section) => {
  const ctx = useContext(LanguageContext);
  if (!section) return ctx;
  return {
    lang: ctx.lang,
    setLang: ctx.setLang,
    t: ctx.t?.[section] || {},
  };
};
